import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Flex, useBreakpointValue, useTheme } from '@chakra-ui/react';
import { useSize } from '@chakra-ui/react-use-size';
import { CategoryCard } from 'src/components/molecules';
import { Category, CategoryCardStyle, RootCategory } from 'src/api/v1-api';
import CategoryCardAdaptiveGrid from './category-card-adaptive-grid';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';

interface Props {
  category: Category | RootCategory | null;
  createListing?: boolean;
}

const SCROLL_DISTANCE = 320;

const CategoryCards = ({ category, createListing = false }: Props) => {
  const [showLeftArrow, setShowLeftArrow] = useState<boolean>(false);
  const [showRightArrow, setShowRightArrow] = useState<boolean>(false);
  const isMobileView = useBreakpointValue({ base: true, md: false });
  const elementRef = useRef<HTMLDivElement>(null);
  const dimensions = useSize(elementRef);
  const theme = useTheme();

  const handleScroll = useCallback(() => {
    if (elementRef.current) {
      const { scrollLeft, offsetWidth, scrollWidth } = elementRef.current;

      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft + offsetWidth < scrollWidth);
    }
  }, [setShowLeftArrow, setShowRightArrow]);

  const scroll = (distance: number) => {
    if (elementRef.current) {
      const { scrollLeft, scrollWidth, offsetWidth } = elementRef.current;
      const maxScrollLeft = scrollWidth - offsetWidth;
      let newScrollLeft = scrollLeft + distance;

      if (newScrollLeft < 0) {
        newScrollLeft = 0;
      } else if (newScrollLeft > maxScrollLeft) {
        newScrollLeft = maxScrollLeft;
      }

      elementRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (elementRef.current) {
      handleScroll();
    }
  }, [elementRef]);

  if (
    !category ||
    category.children?.length === 0 ||
    (!category?.functional?.category_cards && !createListing)
  ) {
    return null;
  }

  // Filter out subcategories that have a position below 0
  category.children = category.children?.filter((subcategory) => subcategory.position >= 0);

  // Sort subcategories by position first, then by created_at
  category.children?.sort((a, b) => {
    if (a.position === b.position) {
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }
    return a.position - b.position;
  });

  const items = category?.children.filter((cat) => cat.image) ?? [];

  const renderCategoryCards = () => {
    const category_card_style_mobile =
      category?.functional?.category_card_style_mobile ?? CategoryCardStyle.STACKED;
    const category_card_style_desktop =
      category?.functional?.category_card_style_desktop ?? CategoryCardStyle.STACKED;
    if (
      (isMobileView && category_card_style_mobile === CategoryCardStyle.STACKED) ||
      (!isMobileView && category_card_style_desktop === CategoryCardStyle.STACKED)
    ) {
      return (
        <Flex direction="column" gap={{ base: 3, md: 4, lg: 5 }}>
          <CategoryCardAdaptiveGrid
            items={items}
            onRender={(subcategory) => (
              <CategoryCard
                key={subcategory.id}
                categoryId={subcategory.id.toString()}
                name={subcategory.name}
                description={subcategory.description}
                imageURL={subcategory.image}
                cta={{
                  label: subcategory.cta_text,
                  link: subcategory.cta_link,
                  is_external: subcategory?.cta_link?.length > 0,
                }}
                createListing={createListing}
              />
            )}
          ></CategoryCardAdaptiveGrid>
        </Flex>
      );
    }

    if (
      (isMobileView && category_card_style_mobile === CategoryCardStyle.HORIZONTAL) ||
      (!isMobileView && category_card_style_desktop === CategoryCardStyle.HORIZONTAL)
    ) {
      return (
        <Flex
          direction="row"
          gap={{ base: 3, md: 4, lg: 5 }}
          scrollSnapType="x mandatory"
          overflowX="auto"
          ref={elementRef}
          onScroll={handleScroll}
        >
          {items.map((subcategory) => (
            <CategoryCard
              key={subcategory.id}
              categoryId={subcategory.id.toString()}
              name={subcategory.name}
              description={subcategory.description}
              imageURL={subcategory.image}
              cta={{
                label: subcategory.cta_text,
                link: subcategory.cta_link,
                is_external: subcategory?.cta_link?.length > 0,
              }}
              createListing={createListing}
              height={isMobileView ? '200px' : '240px'}
              width={
                isMobileView
                  ? `${Math.ceil((dimensions?.width ?? 250) / 1.5)}px`
                  : `${Math.ceil((dimensions?.width ?? 250) / 2.5)}px`
              }
            />
          ))}
        </Flex>
      );
    }
  };

  return items.length !== 0 ? (
    <Flex position="relative" direction={'column'} gap={4}>
      {showLeftArrow && (
        <Box
          position="absolute"
          left="-1"
          top="0"
          w={8}
          h="110%"
          zIndex="1"
          backgroundImage={`linear-gradient(to right, ${theme.colors.mainBG}, transparent)`}
        />
      )}
      {showLeftArrow && !isMobileView && (
        <ChevronLeftIcon
          position="absolute"
          left={2}
          top="calc(50% - 16px)"
          w={8}
          h={8}
          zIndex="2"
          backgroundColor="gray.700"
          color="white"
          borderRadius={4}
          cursor="pointer"
          onClick={() => scroll(-SCROLL_DISTANCE)}
        />
      )}
      {renderCategoryCards()}
      {showRightArrow && !isMobileView && (
        <ChevronRightIcon
          position="absolute"
          right={2}
          top="calc(50% - 16px)"
          w={8}
          h={8}
          zIndex="2"
          backgroundColor="gray.700"
          color="white"
          borderRadius={4}
          cursor="pointer"
          onClick={() => scroll(SCROLL_DISTANCE)}
        />
      )}
      {showRightArrow && (
        <Box
          position="absolute"
          right="-1"
          top="0"
          w={8}
          h="110%"
          zIndex="1"
          backgroundImage={`linear-gradient(to left, ${theme.colors.mainBG}, transparent)`}
        />
      )}
    </Flex>
  ) : null;
};

export default CategoryCards;
